import React, { useState, useRef, useEffect, useCallback } from "react";
import ModerationVisitHeader from "../../components/ModerationVisitHeader";
import ModerationTypeSelector from "../../components/form/ModerationTypeSelector";
import UnitsSelector from "../../components/form/UnitsSelector";
import PreferredDatesSelector from "../../components/form/PreferredDatesSelector";
import ConsortiumQuestion from "../../components/form/ConsortiumQuestion";
import ModerationWindowSelector from "../../components/form/ModerationWindowSelector";
import ContactInformation from "../../components/form/ContactInformation";
import "./ModerationVisitRequest.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSubject } from "../../context/SubjectContext";
import { useConsortiaCenter } from "../../context/ConsortiaCenterContext";
import { useUnits } from "../../context/UnitsContext";
import isEqual from "lodash.isequal";
import { useCenter } from "../../context/CenterContext";
import { useApi } from "../../context/ApiClient";
import {
  useModerationWindowSession,
  VISIT_STATUS,
  MODERATION_TYPE
} from "../../constants/Constants";
import { useModerationWindowDates } from "../../context/ModerationWindowDatesContext";
import {
  getCenterType,
  checkConsortiaUnitMix,
  getRegistrationKeys,
} from "../../utils/utils";

const formatDateToYYYYMMDD = (dateStr) => {
  const date = new Date(dateStr);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const ModerationVisitRequestForm = () => {
  const { consortiaCentres = [] } = useConsortiaCenter();
  const { consortiaUnits = [] } = useUnits();

  const moderationWindowSession = useModerationWindowSession();
  const api = useApi(process.env.REACT_APP_VAF_BASE_URL);
  const { subjectData } = useSubject();
  const { centerNumber } = useCenter();
  const subjectId = subjectData?.selectedSubjectId;
  const currentCentreType =
    getCenterType(
      consortiaCentres,
      Number(centerNumber),
      subjectData?.selectedSubject,
      subjectId,
    ) || "";
  const LOCAL_STORAGE_KEY = `moderationFormData_${centerNumber}_${subjectId}`;
  const [duplicateUnits, setDuplicateUnits] = useState([]);

  // Detect if subject is Oracy
  const isOracy = (() => {
    const sub = subjectData?.selectedSubject;
    if (typeof sub === "string") {
      return sub.trim().toLowerCase().includes("oracy");
    }
    if (sub && typeof sub.name === "string") {
      return sub.name.trim().toLowerCase().includes("oracy");
    }
    return false;
  })();

  // Initialize formData with localStorage data immediately
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsedData = JSON.parse(saved);
        // Always force moderationType to VIRTUAL for Oracy
        if (isOracy) {
          parsedData.moderationType = MODERATION_TYPE.VIRTUAL;
        }
        return parsedData;
      } catch (e) {
        console.error("Failed to parse saved form data:", e);
      }
    }

    return {
      moderationType: isOracy ? MODERATION_TYPE.VIRTUAL : "",
      selectedUnits: [],
      moderationWindow: "window1",
      preferredDates: [],
      isConsortium: "no",
      firstName: "",
      lastName: "",
      email: "",
      telephone: "",
      secondaryFirstName: "",
      secondaryLastName: "",
      secondaryEmail: "",
      secondaryTelephone: "",
      showSecondaryContact: false,
    };
  });

  const navigate = useNavigate();
  const contactRef = useRef(null);
  const [showErrors, setShowErrors] = useState(false);
  const { isMixed, isConsortiumRegistered, isConsortiumUnitsSelected } =
    checkConsortiaUnitMix(
      formData.selectedUnits,
      consortiaUnits,
      moderationWindowSession,
      formData.moderationWindow,
      formData.isConsortium,
    );

  const [showConsortiaError, setShowConsortiaError] = useState(false);
  const { subject } = useParams();
  const location = useLocation();

  const { fetchPreferredDates } = useModerationWindowDates();

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "selectedUnits") {
        // If none of the selected units are in duplicateUnits, clear the error
        const stillHasDuplicates = value.some((unit) =>
          duplicateUnits.includes(unit),
        );
        if (!stillHasDuplicates) {
          setDuplicateUnits([]);
        }
        if (currentCentreType === "Lead") {
          //If none of the selected units create a consortia mix, clear the error
          const { isMixed, isConsortiumRegistered } = checkConsortiaUnitMix(
            value,
            consortiaUnits,
            moderationWindowSession,
            newData.moderationWindow,
          );
          if (!isMixed) {
            setShowConsortiaError(false);
          }
          if (!isConsortiumRegistered) setShowConsortiaError(false);
        }
      }
      if (field === "isConsortium" && currentCentreType === "Lead") {
        if (value === "no" && !isMixed) {
          setShowConsortiaError(false);
        }
      }

      // Clear preferred dates when moderation window changes
      if (field === "moderationWindow") {
        newData.preferredDates = [];
      }

      // Force moderationType to VIRTUAL for Oracy no matter what
      if (isOracy) {
        newData.moderationType = MODERATION_TYPE.VIRTUAL;
      }

      return newData;
    });
  };

  // Save automatically whenever formData changes
  const previousFormDataRef = useRef(formData);
  useEffect(() => {
    if (!isEqual(previousFormDataRef.current, formData)) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formData));
      previousFormDataRef.current = formData; // update the reference
    }
  }, [formData]);

  // Always force to VIRTUAL for Oracy if subject changes
  useEffect(() => {
    if (isOracy && formData.moderationType !== MODERATION_TYPE.VIRTUAL) {
      setFormData((prev) => ({
        ...prev,
        moderationType: MODERATION_TYPE.VIRTUAL
      }));
    }
    // eslint-disable-next-line
  }, [subjectData]);

  // Function to handle contact data updates (memoized to prevent infinite re-renders)
  const handleContactDataChange = useCallback((contactData) => {
    setFormData((prev) => ({
      ...prev,
      ...contactData,
    }));
  }, []);

  useEffect(() => {
    fetchPreferredDates(moderationWindowSession.window1);
    fetchPreferredDates(moderationWindowSession.window2);
    // eslint-disable-next-line
  }, []);

  const handleReviewSubmit = useCallback(async () => {
    if (currentCentreType === "Lead") {
      const { isMixed, isConsortiumRegistered, isConsortiumUnitsSelected } =
        checkConsortiaUnitMix(
          formData.selectedUnits,
          consortiaUnits,
          moderationWindowSession,
          formData.moderationWindow,
          formData.isConsortium,
        );
      const registrationKeys = getRegistrationKeys(
        currentCentreType,
        subjectData.subjectDetails,
      );
      const { isConsortiaRestCandidateRegistered } = registrationKeys;

      setShowConsortiaError(
        isMixed ||
          isConsortiumRegistered ||
          (isConsortiumUnitsSelected &&
            Boolean(isConsortiaRestCandidateRegistered === false)) ||
          (isConsortiumUnitsSelected && formData.isConsortium === "no"),
      );

      if (
        isMixed ||
        isConsortiumRegistered ||
        (isConsortiumUnitsSelected &&
          Boolean(isConsortiaRestCandidateRegistered === false)) ||
        (isConsortiumUnitsSelected && formData.isConsortium === "no")
      ) {
        return;
      }
    }
    // Show validation errors if any
    setShowErrors(true);

    // Validate contact info
    const contactResult = await contactRef.current.getValidatedData();
    const { isValid, data: contactData } = contactResult;

    const isMainFormValid =
      formData.moderationType && formData.selectedUnits.length > 0;

    if (!isValid || !isMainFormValid) {
      // Validation failed: do not navigate
      return;
    }

    const rawForm = { ...formData, ...contactData };
    // 💡 Transform to API format here
    const transformedForm = {
      moderationType: rawForm.moderationType,
      selectedUnits: rawForm.selectedUnits.filter(
        (code) => code && code.length <= 4,
      ),
      moderationWindow:
        rawForm.moderationWindow === "window1"
          ? moderationWindowSession.window1
          : moderationWindowSession.window2,
      preferredDates: rawForm.preferredDates.map(formatDateToYYYYMMDD),
      isConsortium: rawForm.isConsortium === "yes" ? "Y" : "N",
      primaryFirstName: rawForm.firstName,
      primaryLastName: rawForm.lastName,
      primaryEmailId: rawForm.email,
      primaryTelephoneNo: rawForm.telephone,
      isSecondaryContact: rawForm.showSecondaryContact || false,
      secondaryFirstName: rawForm.secondaryFirstName,
      secondaryLastName: rawForm.secondaryLastName,
      secondaryEmailId: rawForm.secondaryEmail,
      secondaryTelephoneNo: rawForm.secondaryTelephone,
      centreNo: Number.parseInt(centerNumber),
      subjectId: Number.parseInt(subjectData.selectedSubjectId),
      subjectName: subjectData.selectedSubject,
      visitStatus: VISIT_STATUS.REQUESTED,
    };

    if (rawForm.visitArrangementId) {
      transformedForm["visitArrangementId"] = rawForm.visitArrangementId;
      transformedForm["updatedBy"] = "test";
    } else {
      transformedForm["insertedBy"] = "test";
    }
    setFormData(rawForm);

    try {
      const response = await api.post(
        `validateModerationVisit`,
        transformedForm,
      );

      // Assuming your API wraps the response in `data`
      const res = response.data;

      if (
        res.isSuccess === false &&
        res.statusCode === 400 &&
        Array.isArray(res.data) &&
        res.data.length > 0
      ) {
        setDuplicateUnits(res.data);
        return; // Do not navigate, just show error
      }

      if (res.isSuccess === true) {
        // No error, continue navigation
        const subjectSlug = subject
          ?.toLowerCase()
          .replaceAll(/[^a-z0-9]+/g, "_") // Replaces all non-alphanumeric characters with underscores.
          .replaceAll(/^(?:_+|_+)$/g, ""); // Removes leading and trailing underscores.
        navigate(`/dashboard/visit_form/review_visit/${subjectSlug}`, {
          state: transformedForm,
        });
        return;
      }
      // Optional: handle other errors
      // setDuplicateUnits(["Unexpected error"]); // Or show a generic error
    } catch (err) {
      if (
        err.response &&
        err.response.status === 400 &&
        err.response.data &&
        Array.isArray(err.response.data.data) &&
        Array.isArray(err.response.data.data) &&
        err.response.data.data.length > 0
      ) {
        setDuplicateUnits(err.response.data.data);
        return; // Do not navigate, just show error
      }
    }
  });
  // Build URL slug

  return (
    <div className="page-layout">
      <ModerationVisitHeader
        formData={formData}
        onReviewSubmit={handleReviewSubmit}
        onBack={() => {
          const confirmLeave = window.confirm(
            `You are about to leave the visit request page.\nAny changes you have made will be lost.\n\nAre you sure you want to continue?`,
          );
          if (confirmLeave) {
            // Clear localStorage when leaving
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            // Redirect to subject landing page
            navigate("/dashboard");
          }
        }}
        duplicateUnits={duplicateUnits}
        selectedUnits={formData.selectedUnits}
        showConsortiaError={showConsortiaError}
        currentCentreType={currentCentreType}
        isConsortiumRegistered={isConsortiumRegistered}
        isMixed={isMixed}
        isConsortiumUnitsSelected={isConsortiumUnitsSelected}
      />

      <div className="columns is-gapless marginadjust">
        {/* Left Column - Main Form */}
        <div className="column is-7">
          <div className="box-style box has-background-white-ter mr-4">
            {/* Only render the moderation type selector if NOT Oracy*/}
            {/* Only render the moderation type selector and hr if NOT Oracy*/}
{!isOracy && (
  <>
    <ModerationTypeSelector
      value={formData.moderationType}
      onChange={(value) => handleChange("moderationType", value)}
      showErrors={showErrors}
    />
    <hr className="mt-1" />
  </>
)}
            <UnitsSelector
              subjectData={subjectData}
              selectedUnits={formData.selectedUnits}
              onChange={(value) => handleChange("selectedUnits", value)}
              showErrors={showErrors}
              selectedWindow={formData.moderationWindow}
              currentCentreType={currentCentreType}
            />
            <hr className="mt-1" />
            <ModerationWindowSelector
              value={formData.moderationWindow}
              onChange={(value) => handleChange("moderationWindow", value)}
              showErrors={showErrors}
            />
            <hr className="mt-1" />
            <PreferredDatesSelector
              selectedDates={formData.preferredDates || []}
              onChange={(value) => handleChange("preferredDates", value)}
              moderationWindow={formData.moderationWindow}
              showErrors={showErrors}
            />
          </div>
        </div>

        {/* Right Column - Consortium Question and Contact Info */}
        <div className="column is-5">
          <div className="box-style box has-background-white-ter">
            <ConsortiumQuestion
              value={formData.isConsortium}
              onChange={(value) => handleChange("isConsortium", value)}
              showErrors={showErrors}
            />
            {formData.isConsortium === "yes" && (
              <div
                style={{
                  padding: "1em",
                  marginBottom: "1rem",
                  borderRadius: "4px",
                  backgroundColor: "#F5F5F5",
                }}
              >
                Make sure your centre has completed the centre consortium
                arrangements form within the JCQ Centre Admin Portal (CAP) for
                this moderation window.
              </div>
            )}
            <hr className="mt-1" />
            <ContactInformation
              formData={formData}
              setFormData={setFormData}
              onContactDataChange={handleContactDataChange}
              ref={contactRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModerationVisitRequestForm;